/**
 * Tab2CasualtyPreview.jsx v1.0.0 - 2026-10-13 (Tab 2 plan 7.11: end-of-tab summary card)
 *
 * Preview of the casualty picture of Tab 2 with the settings on screen (nothing is saved): one run of the calculator like the preliminary
 * pass of Tab 4. Per day: phase, the factors of the casualty rate (form at the enemy resistance x maneuver group x special condition),
 * battle casualties, killed, died of wounds, non-battle cases, time to surgery, and the threat factors (road, helicopter, rear).
 * Brigades are added per day; the factors and the time to surgery are those of the first brigade.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper
} from '@mui/material';
import { calculateCasualties } from '../calculationEngine/casualtyCalculator';
import HelpText from './HelpText';

const f2 = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toFixed(2) : '-');

const Tab2CasualtyPreview = ({ getScenario }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ status: 'idle' });

  const run = () => {
    try {
      const result = calculateCasualties(getScenario(), { final: false });
      setState({ status: 'done', result });
    } catch (e) {
      setState({ status: 'error', message: e.message });
    }
  };

  const rows = [];
  let totals = null;
  if (state.status === 'done') {
    const brigades = state.result.by_brigade || [];
    const first = brigades[0];
    const days = first ? first.by_day.length : 0;
    for (let i = 0; i < days; i += 1) {
      const sum = (pick) => brigades.reduce((s, b) => s + (b.by_day[i] ? pick(b.by_day[i]) : 0), 0);
      const d = first.by_day[i];
      rows.push({
        day: d.day,
        phase: d.phase_name || '',
        profile: d.casualty_profile,
        threat: d.threat_model,
        battle: sum((x) => x.battle.total),
        kia: sum((x) => x.battle.kia),
        dow: sum((x) => x.battle.dow),
        dnbi: sum((x) => x.dnbi.total),
        tts: d.golden_hour ? d.golden_hour.cumulative_time_to_surgery_min : null,
        evac: d.golden_hour ? d.golden_hour.evacuation_profile : null
      });
    }
    totals = rows.reduce((s, r) => ({ battle: s.battle + r.battle, kia: s.kia + r.kia, dow: s.dow + r.dow, dnbi: s.dnbi + r.dnbi }), { battle: 0, kia: 0, dow: 0, dnbi: 0 });
  }

  return (
    <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.light' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">{t('tab2.preview.title')}</Typography>
          <Button variant="contained" onClick={run}>{t('tab2.preview.run')}</Button>
        </Box>
        <HelpText id="summary_card" />
        {state.status === 'error' && (
          <Alert severity="error" sx={{ mt: 1 }}>{t('tab2.preview.error')}: {state.message}</Alert>
        )}
        {state.status === 'done' && (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('tab2.preview.day')}</TableCell>
                  <TableCell>{t('tab2.preview.phase')}</TableCell>
                  <TableCell>{t('tab2.preview.form')}</TableCell>
                  <TableCell align="right">{t('tab2.preview.battle')}</TableCell>
                  <TableCell align="right">{t('tab2.preview.killed')}</TableCell>
                  <TableCell align="right">{t('tab2.preview.dow')}</TableCell>
                  <TableCell align="right">{t('tab2.preview.dnbi')}</TableCell>
                  <TableCell align="right">{t('tab2.preview.tts')}</TableCell>
                  <TableCell>{t('tab2.preview.road')} / {t('tab2.preview.air')} / {t('tab2.preview.rear')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.day}>
                    <TableCell>{r.day}</TableCell>
                    <TableCell>{r.phase}</TableCell>
                    <TableCell>
                      {r.profile
                        ? `${r.profile.form} @ ${r.profile.resistance_level} ${f2(r.profile.resistance_index)} x group ${f2(r.profile.group_factor)} x ${r.profile.condition} ${f2(r.profile.condition_factor)} = ${f2(r.profile.total_factor)}`
                        : t('tab2.preview.march')}
                    </TableCell>
                    <TableCell align="right">{r.battle}</TableCell>
                    <TableCell align="right">{r.kia}</TableCell>
                    <TableCell align="right">{r.dow}</TableCell>
                    <TableCell align="right">{r.dnbi}</TableCell>
                    <TableCell align="right">{r.tts}</TableCell>
                    <TableCell>
                      {r.threat
                        ? `${r.threat.road_status || '-'} ${r.threat.road_route || ''} / x${f2(r.threat.helicopter_availability)} / x${f2(r.threat.rear_factor)}`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {totals && (
                  <TableRow>
                    <TableCell colSpan={3}><strong>{t('tab2.preview.totals')}</strong></TableCell>
                    <TableCell align="right"><strong>{totals.battle}</strong></TableCell>
                    <TableCell align="right"><strong>{totals.kia}</strong></TableCell>
                    <TableCell align="right"><strong>{totals.dow}</strong></TableCell>
                    <TableCell align="right"><strong>{totals.dnbi}</strong></TableCell>
                    <TableCell />
                    <TableCell><Chip size="small" label={`${totals.battle + totals.dnbi}`} /></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default Tab2CasualtyPreview;
